import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { INCOME_CATEGORIES, EXPENSE_CATEGORIES } from "@/lib/entities";
import { formatMoney } from "@/lib/format";
import { useCurrency } from "@/context/CurrencyContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FileUp, Loader2, Sparkles, Banknote, ArrowDownLeft, ArrowUpRight } from "lucide-react";

const TYPE_OPTIONS = ["income", "expense", "transfer", "sweep", "fd", "ignore"];

const STAGE_DESCRIPTIONS = {
  pick: "Pick the account this statement belongs to, choose a file, and let AI tag the transactions.",
  preview: "Confirm the detected columns and row counts look right before AI tagging.",
  review: "Review the AI tags, adjust as needed, then post. Credits add to the balance, debits subtract.",
};

export function StatementImporter({ onPosted }) {
  const { currency } = useCurrency();
  const [open, setOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [posting, setPosting] = useState(false);
  const [rows, setRows] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [tagProgress, setTagProgress] = useState(0);
  const [pageIdx, setPageIdx] = useState(0);
  const [closingBalance, setClosingBalance] = useState(null); // detected from statement (nullable)
  const [applyClosing, setApplyClosing] = useState(true);
  const PAGE_SIZE = 100;

  useEffect(() => {
    if (!open) return;
    api.get("/entities/bank-accounts").then(({ data }) => {
      const list = Array.isArray(data) ? data : [];
      setAccounts(list);
      setAccountId((cur) => cur || (list[0]?.id ?? ""));
    }).catch((e) => {
      console.error("Failed to load bank accounts", e);
    });
  }, [open]);

  const account = accounts.find((a) => a.id === accountId);
  const accCurrency = account?.currency || currency;

  const reset = () => {
    setFile(null);
    setRows(null);
    setPreview(null);
    setPageIdx(0);
    setParsing(false);
    setPreviewing(false);
    setPosting(false);
    setClosingBalance(null);
    setApplyClosing(true);
  };

  const previewFile = async () => {
    if (!file) return toast.error("Choose a statement file first");
    setPreviewing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/bank-statements/preview", fd);
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Could not read statement");
    } finally {
      setPreviewing(false);
    }
  };

  const parse = async () => {
    if (!file) return toast.error("Choose a statement file first");
    setParsing(true);
    setTagProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/bank-statements/parse-async", fd);
      // Capture the statement's closing balance up-front (available in the sync
      // parse response for tabular files, present on the job doc for PDFs).
      if (typeof data.closing_balance === "number") setClosingBalance(data.closing_balance);
      let job = data;
      const started = Date.now();
      while (job.status === "processing" && Date.now() - started < 1800000) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const res = await api.get(`/bank-statements/parse-jobs/${job.job_id || data.job_id}`);
          job = { ...res.data, job_id: data.job_id };
          setTagProgress(job.progress || 0);
        } catch (e) {
          console.error("Tagging status poll failed, retrying", e);
        }
      }
      if (job.status === "error") throw new Error(job.error || "Could not parse statement");
      if (job.status !== "done") throw new Error("AI tagging is taking too long — please try again");
      const txns = (job.transactions || []).map((t, idx) => {
        const type = TYPE_OPTIONS.includes(t.type)
          ? t.type
          : t.direction === "credit" ? "income" : "expense";
        const cats = type === "income" ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;
        let category;
        if (type === "sweep") category = "Sweep";
        else if (type === "fd") category = "Fixed Deposit";
        else category = cats.includes(t.category) ? t.category : "Other";
        return { ...t, type, category, _uid: `txn-${idx}-${t.date}-${t.amount}` };
      });
      if (!txns.length) {
        toast.error("No transactions found in this file");
      } else {
        toast.success(`Parsed ${txns.length} transactions · AI tagged`);
      }
      setPageIdx(0);
      setRows(txns);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Could not parse statement");
    } finally {
      setParsing(false);
    }
  };

  const setRow = (i, patch) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const post = async () => {
    if (!accountId) return toast.error("Select a bank account");
    setPosting(true);
    try {
      const payload = {
        bank_account_id: accountId,
        transactions: rows,
      };
      if (applyClosing && typeof closingBalance === "number") {
        payload.closing_balance = closingBalance;
      }
      const { data } = await api.post("/recon/stage", payload);
      toast.success(
        `${data.staged} bank line(s) staged for matching${
          data.skipped_duplicates ? ` · ${data.skipped_duplicates} duplicate(s) skipped` : ""
        } · balance ${data.balance_delta >= 0 ? "+" : ""}${formatMoney(data.balance_delta, data.currency)}${
          data.closing_balance_applied != null ? " · closing balance applied" : ""
        } — open Bank Reconciliation to confirm invoice matches`
      );
      if (data.closing_balance_skipped_reason) {
        toast.warning(data.closing_balance_skipped_reason);
      }
      setOpen(false);
      reset();
      onPosted && onPosted();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not post transactions");
    } finally {
      setPosting(false);
    }
  };

  const counts = rows
    ? rows.reduce((a, r) => ((a[r.type] = (a[r.type] || 0) + 1), a), {})
    : {};

  let stage = "pick";
  if (rows) stage = "review";
  else if (preview) stage = "preview";

  return (
    <Card className="p-5" data-testid="statement-importer">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-md bg-brand/10 border border-brand/25 flex items-center justify-center shrink-0">
            <FileUp className="h-5 w-5 text-brand" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
              Import Bank Statement
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-brand2 border border-brand2/40 rounded-full px-2 py-0.5">
                <Sparkles className="h-3 w-3" /> AI tagging
              </span>
            </h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              Upload CSV, Excel or PDF. AI tags each line as income/expense, then posts to your ledgers & updates the balance.
            </p>
          </div>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="open-statement-import-btn" className="gap-2 shrink-0">
          <FileUp className="h-4 w-4" /> Upload Statement
        </Button>
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Import & Tag Bank Statement</DialogTitle>
            <DialogDescription>{STAGE_DESCRIPTIONS[stage]}</DialogDescription>
          </DialogHeader>

          {stage === "pick" && (
            <div className="grid sm:grid-cols-2 gap-4 py-2">
              <div>
                <Label className="text-xs mb-1.5 block">Bank Account</Label>
                <Select value={accountId} onValueChange={setAccountId}>
                  <SelectTrigger data-testid="statement-account-select">
                    <SelectValue placeholder="Select account" />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.name} · {a.bank} ({a.currency})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs mb-1.5 block">Statement file (.csv, .xlsx, .pdf)</Label>
                <Input
                  type="file"
                  accept=".csv,.xlsx,.xls,.pdf"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  data-testid="statement-file-input"
                />
              </div>
            </div>
          )}
          {stage === "preview" && (
            <ImportPreview preview={preview} fileName={file?.name} accountLabel={`${account?.name || ""} (${accCurrency})`} />
          )}
          {stage === "review" && (
            <div className="py-1">
              <div className="flex flex-wrap items-center gap-2 mb-3 text-xs">
                <span className="text-muted-foreground">{rows.length} transactions ·</span>
                <Badge tone="success">{counts.income || 0} income</Badge>
                <Badge tone="danger">{counts.expense || 0} expense</Badge>
                <Badge tone="muted">{counts.transfer || 0} transfer</Badge>
                <Badge tone="muted">{counts.sweep || 0} sweep</Badge>
                <Badge tone="muted">{counts.fd || 0} FD</Badge>
                <Badge tone="muted">{counts.ignore || 0} ignored</Badge>
                <span className="text-muted-foreground ml-auto">Posting to: <span className="text-foreground font-medium">{account?.name} ({accCurrency})</span></span>
              </div>
              {typeof closingBalance === "number" && (
                <div className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-brand/30 bg-brand/5 px-3 py-2 text-xs" data-testid="closing-balance-banner">
                  <span className="text-muted-foreground">Closing balance detected in statement:</span>
                  <span className="font-mono font-medium text-foreground">{formatMoney(closingBalance, accCurrency)}</span>
                  <label className="ml-auto inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={applyClosing}
                      onChange={(e) => setApplyClosing(e.target.checked)}
                      data-testid="apply-closing-balance"
                      className="h-3.5 w-3.5"
                    />
                    <span className="text-muted-foreground">Set account balance to this value (recommended)</span>
                  </label>
                </div>
              )}
              <div className="max-h-[52vh] overflow-auto rounded-md border border-border" data-testid="statement-review-table">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-card/95 backdrop-blur z-10">
                    <tr className="text-[11px] uppercase tracking-wider text-muted-foreground">
                      <th className="text-left font-medium px-3 py-2">Date</th>
                      <th className="text-left font-medium px-3 py-2">Description</th>
                      <th className="text-right font-medium px-3 py-2">Amount</th>
                      <th className="text-left font-medium px-3 py-2 w-[120px]">Type</th>
                      <th className="text-left font-medium px-3 py-2 w-[150px]">Category</th>
                      <th className="text-left font-medium px-3 py-2 w-[160px]">Vendor / Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(pageIdx * PAGE_SIZE, (pageIdx + 1) * PAGE_SIZE).map((r, idx) => {
                      const i = pageIdx * PAGE_SIZE + idx;
                      const cats = r.type === "income" ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;
                      const disabled = r.type === "transfer" || r.type === "sweep" || r.type === "ignore";
                      return (
                        <tr key={r._uid || i} className="border-t border-border hover:bg-muted/40" data-testid={`statement-row-${i}`}>
                          <td className="px-3 py-2 font-mono text-xs text-muted-foreground whitespace-nowrap">{r.date || "—"}</td>
                          <td className="px-3 py-2 max-w-[260px] truncate" title={r.description}>{r.description || "—"}</td>
                          <td className="px-3 py-2 text-right font-mono whitespace-nowrap">
                            <span className={`inline-flex items-center gap-1 ${r.direction === "credit" ? "text-success" : "text-danger"}`}>
                              {r.direction === "credit" ? <ArrowDownLeft className="h-3 w-3" /> : <ArrowUpRight className="h-3 w-3" />}
                              {formatMoney(r.amount, accCurrency)}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={r.type}
                              onChange={(e) => setRow(i, { type: e.target.value })}
                              data-testid={`statement-type-${i}`}
                              className="w-full bg-background border border-input rounded-md px-2 py-1.5 text-xs capitalize focus:outline-none focus:ring-1 focus:ring-ring"
                            >
                              {TYPE_OPTIONS.map((o) => (
                                <option key={o} value={o}>{o}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={cats.includes(r.category) ? r.category : "Other"}
                              onChange={(e) => setRow(i, { category: e.target.value })}
                              disabled={disabled}
                              data-testid={`statement-category-${i}`}
                              className="w-full bg-background border border-input rounded-md px-2 py-1.5 text-xs disabled:opacity-40 focus:outline-none focus:ring-1 focus:ring-ring"
                            >
                              {cats.map((c) => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <input
                              value={r.vendor || ""}
                              onChange={(e) => setRow(i, { vendor: e.target.value })}
                              disabled={disabled}
                              data-testid={`statement-vendor-${i}`}
                              className="w-full bg-background border border-input rounded-md px-2 py-1.5 text-xs disabled:opacity-40 focus:outline-none focus:ring-1 focus:ring-ring"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {rows.length > PAGE_SIZE && (
                <div className="flex items-center justify-between mt-2 text-xs" data-testid="review-pagination">
                  <span className="text-muted-foreground">
                    Showing {pageIdx * PAGE_SIZE + 1}–{Math.min((pageIdx + 1) * PAGE_SIZE, rows.length)} of {rows.length}
                  </span>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" disabled={pageIdx === 0}
                      onClick={() => setPageIdx((p) => p - 1)} data-testid="review-prev-page">Prev</Button>
                    <Button size="sm" variant="outline" disabled={(pageIdx + 1) * PAGE_SIZE >= rows.length}
                      onClick={() => setPageIdx((p) => p + 1)} data-testid="review-next-page">Next</Button>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {stage === "review" && (
              <>
                <Button variant="outline" onClick={() => setRows(null)} disabled={posting}>Back</Button>
                <Button onClick={post} disabled={posting} data-testid="post-statement-btn" className="gap-2">
                  {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Banknote className="h-4 w-4" />}
                  Post to Ledgers
                </Button>
              </>
            )}
            {stage === "preview" && (
              <>
                <Button variant="outline" onClick={() => setPreview(null)} disabled={parsing} data-testid="preview-back-btn">Back</Button>
                <Button
                  onClick={parse}
                  disabled={parsing || (preview.file_type === "tabular" && preview.will_import === 0)}
                  data-testid="confirm-import-btn"
                  className="gap-2"
                >
                  {parsing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  {parsing ? (tagProgress > 0 ? `AI tagging… ${tagProgress}%` : "Parsing & tagging…") : "Confirm & AI-Tag"}
                </Button>
              </>
            )}
            {stage === "pick" && (
              <>
                <Button variant="outline" onClick={() => { setOpen(false); reset(); }}>Cancel</Button>
                <Button onClick={previewFile} disabled={previewing || !file || !accountId} data-testid="preview-statement-btn" className="gap-2">
                  {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
                  {previewing ? "Reading file…" : "Preview Import"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function Badge({ tone, children }) {
  const cls =
    tone === "success" ? "bg-success/15 text-success border-success/30"
    : tone === "danger" ? "bg-danger/15 text-danger border-danger/30"
    : "bg-muted text-muted-foreground border-border";
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 ${cls}`}>{children}</span>;
}

const MAPPING_LABELS = [
  ["date", "Date"],
  ["description", "Description"],
  ["debit", "Debit / Withdrawal"],
  ["credit", "Credit / Deposit"],
  ["amount", "Amount"],
  ["indicator", "Dr/Cr Indicator"],
];

function MappingRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 text-xs py-1.5 border-b border-border/60 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      {value ? (
        <span className="font-mono text-foreground bg-muted rounded px-1.5 py-0.5 truncate max-w-[180px]" title={value}>“{value}”</span>
      ) : (
        <span className="text-muted-foreground/50 italic">not found</span>
      )}
    </div>
  );
}

function ImportPreview({ preview, fileName, accountLabel }) {
  if (preview.file_type === "pdf") {
    return (
      <div className="py-2 space-y-3" data-testid="import-preview-panel">
        <p className="text-sm">
          <span className="font-medium">{fileName}</span> is a PDF statement ({preview.pages} page{preview.pages === 1 ? "" : "s"}).
        </p>
        <p className="text-sm text-muted-foreground">
          Column mapping doesn&apos;t apply to PDFs — AI will read the document and extract every transaction it finds.
          You&apos;ll still review each line before anything is posted to <span className="text-foreground">{accountLabel}</span>.
        </p>
      </div>
    );
  }
  const m = preview.mapping || {};
  const skipped = preview.skipped_count || 0;
  const sample = preview.sample || [];
  return (
    <div className="py-1 space-y-4" data-testid="import-preview-panel">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Badge tone={preview.will_import > 0 ? "success" : "danger"}>{preview.will_import} rows will import</Badge>
        {skipped > 0 && <Badge tone="danger">{skipped} rows skipped</Badge>}
        {preview.empty_rows > 0 && <Badge tone="muted">{preview.empty_rows} empty rows</Badge>}
        <span className="text-muted-foreground">
          Header detected on row {preview.header_row} · {preview.total_rows} data rows · {fileName}
        </span>
      </div>
      {preview.will_import === 0 && (
        <p className="text-xs text-danger" data-testid="preview-zero-warning">
          No importable rows were detected. Check the column mapping below — if it looks wrong, this file layout may not be supported yet.
        </p>
      )}
      {preview.will_import > (preview.cap || 5000) && (
        <p className="text-xs text-amber-400" data-testid="preview-cap-warning">
          Only the first {preview.cap || 5000} transactions will be imported in one run. Split larger statements into multiple files.
        </p>
      )}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="rounded-md border border-border p-3" data-testid="preview-column-mapping">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-1">We mapped your columns like this</p>
          {MAPPING_LABELS.map(([key, label]) => (
            <MappingRow key={key} label={label} value={m[key]} />
          ))}
        </div>
        <div className="rounded-md border border-border p-3 overflow-auto max-h-[240px]" data-testid="preview-skipped-rows">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-1">Skipped rows{skipped ? ` (${skipped})` : ""}</p>
          {preview.skipped_samples?.length ? (
            <div className="space-y-1.5">
              {preview.skipped_samples.map((s, i) => (
                <div key={`skip-${s.row}-${i}`} className="text-xs">
                  <span className="font-mono text-muted-foreground">Row {s.row}</span>{" "}
                  <span className="text-danger">· {s.reason}</span>
                  <p className="text-muted-foreground/70 truncate" title={s.preview}>{s.preview || "—"}</p>
                </div>
              ))}
              {skipped > preview.skipped_samples.length && (
                <p className="text-xs text-muted-foreground">…and {skipped - preview.skipped_samples.length} more</p>
              )}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground py-4 text-center">No rows skipped — every data row was read.</p>
          )}
        </div>
      </div>
      {sample.length > 0 && (
        <div className="rounded-md border border-border overflow-hidden" data-testid="preview-sample-rows">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium px-3 pt-2.5 pb-1">First {sample.length} transactions</p>
          <table className="w-full text-xs">
            <tbody>
              {sample.map((t, i) => (
                <tr key={`${t.date}-${t.amount}-${i}`} className="border-t border-border/60">
                  <td className="px-3 py-1.5 font-mono text-muted-foreground whitespace-nowrap">{t.date || "—"}</td>
                  <td className="px-3 py-1.5 max-w-[300px] truncate" title={t.description}>{t.description || "—"}</td>
                  <td className={`px-3 py-1.5 text-right font-mono whitespace-nowrap ${t.direction === "credit" ? "text-success" : "text-danger"}`}>
                    {t.direction === "credit" ? "+" : "−"}{t.amount}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        Looks right? Confirm to run AI tagging — you&apos;ll review every transaction before it&apos;s posted to <span className="text-foreground">{accountLabel}</span>.
      </p>
    </div>
  );
}
